import { useQuery } from 'react-query'
import './AddProductPage.style.less'
import {
  cacheProductImagePreview,
  createProduct,
  getProductDetail,
  getProductSuppliers,
  prepareCreateProductPayload,
  prepareUpdateProductPayload,
  transformProductDetailToFormValue,
  updateProduct,
} from '../ProductTransform'
import type {
  ProductCreatePayload,
  ProductDefaultFieldSetting,
  ProductFieldSetting,
  ProductSupplier,
  ProductUpdatePayload,
} from '../ProductTransform'
import {
  Button,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  message,
  Modal,
  notification,
  Select,
  Space,
  Spin,
  Tooltip,
  Upload,
} from 'antd'
import type { UploadProps } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { orderBy } from 'lodash'
import { useAuth } from '@/providers/auth.provider'
import BackHeader from '@/layout/components/BackHeader'
import { useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_ROUTE } from '@/constant/common'
import Header from '@/layout/components/Header'
import {
  DownOutlined,
  EditOutlined,
  LockFilled,
  PictureOutlined,
  PlusOutlined,
  TagOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useMobileSidebar } from '@/layout/components/MobileSidebarContext'
import { USER_ROLE } from '@/constant/user'
import {
  getProductUserRole,
  isProductCreatedBySupplier,
  useProductPermission,
} from '../ProductPermission'
import useMasterData from '@/hook/MasterData'
import { onAddNewPropertyValue } from '@/screens/Order/Pages/Order.transform'
import { useProductFieldPermission } from '../ProductFieldPermission'
import type {
  ProductFieldAccess,
  ProductFieldPermissionTarget,
} from '../ProductFieldPermission.types'
import { getProductFieldPermissionSource } from '../ProductFieldPermission.utils'
import { getProductSelectOptions } from '../ProductSelectOptions'
import { toProductDateFormValue } from '../ProductDateValue'
import * as Sentry from '@sentry/react'
import { isPhoneOrTabletDevice } from '@/utils/device'
import { DecimalInput } from '@/components/DecimalInput'
import { getProductRedirectErrorMessage } from '../ProductError'
import addCameraIcon from '@/assets/images/icons/add-camera.svg'
import addImageIcon from '@/assets/images/icons/add-image.svg'
import { useFreshProductSetting } from '../ProductSettingQuery'
import { Cropper, CropperRef } from 'react-mobile-cropper'
import 'react-mobile-cropper/dist/style.css'

const labelCol = { span: 9 }
const SENTRY_REDACTED_KEY_PATTERN =
  /authorization|access[_-]?token|refresh[_-]?token|password|cookie|data_raw/i

export function shouldFetchProductSuppliers(userRole?: string) {
  return userRole !== USER_ROLE.SUPPLIER
}

function getReadOnlyFormItemProps(isReadOnly: boolean, className?: string) {
  return {
    className: [className, isReadOnly && 'product-read-only-form-item']
      .filter(Boolean)
      .join(' '),
    extra: isReadOnly ? (
      <Tooltip title="Read only">
        <span
          aria-label="Read only field"
          className="product-read-only-indicator"
          role="img"
        >
          <LockFilled />
        </span>
      </Tooltip>
    ) : undefined,
  }
}

const STATIC_PRODUCT_FIELDS: ProductDefaultFieldSetting[] = [
  {
    name: 'Supplier',
    field: 'supplier',
    value: '',
    system: true,
    required: false,
    data_type: 'select',
    display_order: 0,
  },
  {
    name: 'Vendor Style #',
    field: 'number',
    value: '',
    system: true,
    required: true,
    data_type: 'text',
    display_order: 1,
  },
  {
    name: 'Product name',
    field: 'name',
    value: '',
    system: true,
    required: true,
    data_type: 'text',
    display_order: 2,
  },
  {
    name: 'Product image',
    field: 'picture',
    value: '',
    system: true,
    required: true,
    data_type: 'image',
    display_order: 3,
  },
]

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })

function getUploadValueFromEvent(event: any) {
  if (Array.isArray(event)) return event
  if (event?.file?.status === 'removed') {
    const objectUrl = event.file.thumbUrl || event.file.url

    if (objectUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(objectUrl)
    }
  }

  return event?.fileList?.map((item) => {
    const thumbUrl =
      item.thumbUrl ||
      item.url ||
      (item.originFileObj ? URL.createObjectURL(item.originFileObj) : undefined)

    const fileItem = { ...item, status: 'done', thumbUrl }
    // Fire-and-forget: cache the image bytes on the stored form item while
    // the picked file is still readable (see cacheProductImagePreview).
    cacheProductImagePreview(fileItem)

    return fileItem
  })
}

function getFileUploadValueFromEvent(event: any) {
  if (Array.isArray(event)) return event

  return event?.fileList?.map((item) => ({
    ...item,
    status: 'done',
  }))
}

function validateProductFileSize(file: File) {
  if (file.size > 5242880) {
    message.error('Each File cannot be over 5mbps', 3)
    return Upload.LIST_IGNORE
  }

  return false
}

function getFieldDisplayName(
  fieldNames: ProductFieldSetting[] = [],
  field: string,
  fallbackName: string
) {
  const matchedField = fieldNames.find((fieldName) => {
    return fieldName.field === field
  })

  return matchedField?.display_name || fallbackName
}

function trimFormInputValues<T extends Record<string, any>>(values: T): T {
  return Object.entries(values).reduce((trimmedValues, [key, value]) => {
    return {
      ...trimmedValues,
      [key]: typeof value === 'string' ? value.trim() : value,
    }
  }, {} as T)
}

function sanitizeSentryValue(
  value: any,
  depth = 0,
  seen = new WeakSet<object>()
): any {
  if (value === null || value === undefined) return value

  if (typeof value === 'string') {
    if (value.startsWith('data:')) {
      return `[data URL omitted; ${value.length} characters]`
    }

    return value.length > 4000 ? `${value.slice(0, 4000)}...` : value
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    }
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return {
      size: value.size,
      type: value.type,
    }
  }

  if (value instanceof Date) return value.toISOString()
  if (typeof value !== 'object') return String(value)
  if (seen.has(value)) return '[circular reference]'
  if (depth >= 5) return '[maximum depth reached]'

  seen.add(value)

  if (Array.isArray(value)) {
    return value
      .slice(0, 25)
      .map((item) => sanitizeSentryValue(item, depth + 1, seen))
  }

  return Object.entries(value).reduce((sanitized, [key, item]) => {
    return {
      ...sanitized,
      [key]: SENTRY_REDACTED_KEY_PATTERN.test(key)
        ? '[redacted]'
        : sanitizeSentryValue(item, depth + 1, seen),
    }
  }, {})
}

export function ProductImageUpload({
  disabled = false,
  fileList = [],
  onChange,
  showCameraButton = false,
}: Pick<UploadProps, 'disabled' | 'fileList' | 'onChange'> & {
  showCameraButton?: boolean
}) {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState('')
  const [previewImageFile, setPreviewImageFile] = useState<any>(null)
  const [previewOperationsElement, setPreviewOperationsElement] =
    useState<HTMLElement | null>(null)
  const [cropImageSrc, setCropImageSrc] = useState('')
  const [pendingCropFile, setPendingCropFile] = useState<any>(null)
  const [isCroppingImage, setIsCroppingImage] = useState(false)
  const cropperRef = useRef<CropperRef | null>(null)

  const getImageSource = useCallback(async (file: any) => {
    if (file.originFileObj) {
      return URL.createObjectURL(file.originFileObj)
    }

    const imageUrl =
      file.url_path ||
      file.response?.data?.url_path ||
      file.response?.url_path ||
      file.url ||
      file.preview ||
      file.thumbUrl ||
      ''

    if (
      !imageUrl ||
      imageUrl.startsWith('blob:') ||
      imageUrl.startsWith('data:')
    ) {
      return imageUrl
    }

    const response = await fetch(imageUrl, { mode: 'cors' })

    if (!response.ok) {
      throw new Error('Unable to load product image for editing')
    }

    return URL.createObjectURL(await response.blob())
  }, [])

  const openCropperForFile = useCallback(
    async (file: any) => {
      let imageSource = ''

      try {
        imageSource = await getImageSource(file)
      } catch (error) {
        message.error('Unable to load this image for editing.', 3)
        return
      }

      if (!imageSource) return

      setPendingCropFile(file)
      setCropImageSrc(imageSource)
      setIsCroppingImage(true)
    },
    [getImageSource]
  )

  const closeCropper = useCallback(() => {
    if (cropImageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(cropImageSrc)
    }

    setCropImageSrc('')
    setPendingCropFile(null)
    setIsCroppingImage(false)
  }, [cropImageSrc])

  useEffect(() => {
    return () => {
      if (cropImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc)
      }
    }
  }, [cropImageSrc])

  useEffect(() => {
    if (!isPreviewVisible) {
      setPreviewOperationsElement(null)
      return
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setPreviewOperationsElement(
        document.querySelector<HTMLElement>('.ant-image-preview-operations')
      )
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [isPreviewVisible])

  const onPreviewImage = async (file: any) => {
    let previewUrl =
      file.url_path ||
      file.response?.data?.url_path ||
      file.response?.url_path ||
      file.url ||
      file.preview ||
      file.thumbUrl

    if (!previewUrl && file.originFileObj) {
      previewUrl = await getBase64(file.originFileObj)
    }

    if (!previewUrl) return

    setPreviewImageUrl(previewUrl)
    setPreviewImageFile(file)
    setIsPreviewVisible(true)
  }

  const onSelectImage = useCallback(
    (event: any) => {
      if (event?.file?.status === 'removed') {
        onChange?.(event)
        return
      }

      const selectedFile =
        event?.fileList?.[event.fileList.length - 1] || event?.file
      const originFile = selectedFile?.originFileObj

      if (!originFile?.type?.startsWith('image/')) {
        onChange?.(event)
        return
      }

      openCropperForFile(selectedFile)
    },
    [onChange, openCropperForFile]
  )

  const applyCroppedImage = useCallback(() => {
    const canvas = cropperRef.current?.getCanvas()
    const originFile =
      pendingCropFile?.originFileObj ||
      new File([], pendingCropFile?.name || 'product-image.jpg', {
        type: pendingCropFile?.type || 'image/jpeg',
      })

    if (!canvas || !originFile) {
      onChange?.({ file: pendingCropFile, fileList: [pendingCropFile] } as any)
      closeCropper()
      return
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          onChange?.({
            file: pendingCropFile,
            fileList: [pendingCropFile],
          } as any)
          closeCropper()
          return
        }

        const croppedFile = new File([blob], originFile.name, {
          type: blob.type || originFile.type || 'image/jpeg',
          lastModified: Date.now(),
        })
        const croppedImageUrl = URL.createObjectURL(blob)
        const croppedUploadFile = {
          ...pendingCropFile,
          name: pendingCropFile.name || croppedFile.name,
          originFileObj: croppedFile,
          preview: croppedImageUrl,
          response: undefined,
          size: croppedFile.size,
          status: 'done',
          thumbUrl: croppedImageUrl,
          thumbnail_url_path: croppedImageUrl,
          type: croppedFile.type,
          url: croppedImageUrl,
          url_path: croppedImageUrl,
        }
        const nextFileList = fileList.length
          ? fileList.map((file: any) =>
              file.uid === pendingCropFile?.uid ? croppedUploadFile : file
            )
          : [croppedUploadFile]

        onChange?.({
          file: croppedUploadFile,
          fileList: nextFileList,
        } as any)
        setPreviewImageUrl(croppedImageUrl)
        setPreviewImageFile(croppedUploadFile)
        closeCropper()
      },
      originFile.type || 'image/jpeg',
      0.92
    )
  }, [closeCropper, fileList, onChange, pendingCropFile])

  const cropperModal = (
    <Modal
      title="Crop Product Image"
      open={isCroppingImage}
      onCancel={closeCropper}
      onOk={applyCroppedImage}
      okText="Apply"
      cancelText="Cancel"
      maskClosable={false}
      destroyOnClose
      width={720}
      zIndex={1100}
      className="product-image-crop-modal"
    >
      {cropImageSrc && (
        <Cropper
          ref={cropperRef as any}
          src={cropImageSrc}
          className="product-image-cropper"
        />
      )}
    </Modal>
  )

  if (showCameraButton && !disabled && !fileList.length) {
    return (
      <>
        <div className="product-image-mobile-actions">
          <Upload
            accept="image/*"
            beforeUpload={() => false}
            capture="environment"
            className="product-image-mobile-action-upload"
            disabled={disabled}
            fileList={fileList}
            listType="picture-card"
            maxCount={1}
            onChange={onSelectImage}
            showUploadList={false}
          >
            <div
              aria-label="Take photo"
              className="product-image-mobile-action-button"
            >
              <img
                alt=""
                className="product-image-mobile-action-icon"
                src={addCameraIcon}
              />
            </div>
          </Upload>
          <Upload
            accept="image/*"
            beforeUpload={() => false}
            className="product-image-mobile-action-upload"
            disabled={disabled}
            fileList={fileList}
            listType="picture-card"
            maxCount={1}
            onChange={onSelectImage}
            showUploadList={false}
          >
            <div
              aria-label="Choose from library"
              className="product-image-mobile-action-button"
            >
              <img
                alt=""
                className="product-image-mobile-action-icon"
                src={addImageIcon}
              />
            </div>
          </Upload>
        </div>
        {cropperModal}
      </>
    )
  }

  return (
    <>
      <Upload
        fileList={fileList}
        listType="picture-card"
        maxCount={1}
        accept="image/*"
        beforeUpload={() => false}
        disabled={disabled}
        onChange={onSelectImage}
        onPreview={onPreviewImage}
        previewFile={(file) => getBase64(file as File)}
      >
        {fileList.length ? null : (
          <div className="product-image-camera-button">
            <PictureOutlined />
            <span>Library</span>
          </div>
        )}
      </Upload>
      {previewImageUrl && (
        <Image
          key={previewImageUrl}
          alt="Product image preview"
          preview={{
            visible: isPreviewVisible,
            onVisibleChange: (visible) => {
              setIsPreviewVisible(visible)

              if (!visible) {
                setPreviewImageFile(null)
              }
            },
          }}
          src={previewImageUrl}
          wrapperStyle={{ display: 'none' }}
        />
      )}
      {isPreviewVisible &&
        previewImageFile &&
        previewOperationsElement &&
        !disabled &&
        createPortal(
          <li className="ant-image-preview-operations-operation product-image-preview-edit-operation">
            <button
              aria-label="Edit product image from preview"
              className="product-image-preview-edit-button"
              onClick={() => {
                setIsPreviewVisible(false)
                openCropperForFile(previewImageFile)
              }}
              type="button"
            >
              <EditOutlined className="ant-image-preview-operations-icon" />
            </button>
          </li>,
          previewOperationsElement
        )}
      {cropperModal}
    </>
  )
}

export function ProductFileUpload({
  disabled = false,
  fileList = [],
  onChange,
}: Pick<UploadProps, 'disabled' | 'fileList' | 'onChange'>) {
  return (
    <Upload
      accept=".doc,.docx,.docm,.xls,.xlsx,xlsm,.csv,.ppt,.pptx,.ppts,.txt,.rtf,.md,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.svg,.eps,.raw,.psd,.ai,.pdf,.ico,.webp,.gif"
      beforeUpload={validateProductFileSize}
      className="product-file-upload"
      disabled={disabled}
      fileList={fileList}
      maxCount={10}
      multiple
      onChange={onChange}
    >
      <Button
        className="upload-button"
        disabled={disabled}
        icon={<UploadOutlined />}
        size="large"
      >
        Select File
      </Button>
    </Upload>
  )
}

function SupplierMobileSelect(props: {
  disabled?: boolean
  onChange?: (value?: string) => void
  suppliers: ProductSupplier[]
  tooltipTitle?: string
  value?: string
}) {
  const { disabled, onChange, suppliers, tooltipTitle, value } = props
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const selectedSupplier = suppliers.find((supplier) => supplier.id === value)
  const filteredSuppliers = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase()

    if (!normalizedSearchText) return suppliers

    return suppliers.filter((supplier) => {
      return supplier.name.toLowerCase().includes(normalizedSearchText)
    })
  }, [searchText, suppliers])

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <div>
          <button
            type="button"
            className="product-supplier-mobile-select"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            <span
              className={
                selectedSupplier
                  ? 'product-supplier-mobile-select-value'
                  : 'product-supplier-mobile-select-placeholder'
              }
            >
              {selectedSupplier?.name || 'Select'}
            </span>
            <DownOutlined className="product-supplier-mobile-select-arrow" />
          </button>
        </div>
      </Tooltip>
      <Drawer
        className="product-supplier-mobile-drawer"
        destroyOnClose
        height="75vh"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        placement="bottom"
        title="Supplier"
      >
        <div className="product-supplier-mobile-drawer-content">
          <Input
            allowClear
            className="product-supplier-mobile-search"
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search supplier"
            size="large"
            value={searchText}
          />
          {value && (
            <Button
              className="product-supplier-mobile-clear-button"
              onClick={() => {
                onChange?.(undefined)
                setIsOpen(false)
              }}
              type="text"
            >
              Clear selection
            </Button>
          )}
          <div className="product-supplier-mobile-list">
            {filteredSuppliers.map((supplier) => (
              <Button
                className="product-supplier-mobile-list-item"
                key={supplier.id}
                onClick={() => {
                  onChange?.(supplier.id)
                  setIsOpen(false)
                }}
                type="text"
              >
                {supplier.name}
              </Button>
            ))}
            {!filteredSuppliers.length && (
              <p className="product-supplier-mobile-empty">
                No suppliers found
              </p>
            )}
          </div>
        </div>
      </Drawer>
    </>
  )
}

function SupplierSelect(props: {
  disabled?: boolean
  onChange?: (value?: string) => void
  onDropdownVisibleChange: (open: boolean) => void
  suppliers: ProductSupplier[]
  tooltipTitle?: string
  value?: string
}) {
  const {
    disabled,
    onChange,
    onDropdownVisibleChange,
    suppliers,
    tooltipTitle,
    value,
  } = props

  return (
    <Tooltip
      title={tooltipTitle}
      placement="topRight"
      overlayClassName="add-product-supplier-field-tooltip"
    >
      <div>
        <Select
          showSearch
          allowClear
          dropdownClassName="product-supplier-select-dropdown"
          dropdownRender={(menu) => (
            <div
              onTouchMove={(event) => event.stopPropagation()}
              onWheel={(event) => event.stopPropagation()}
            >
              {menu}
            </div>
          )}
          onChange={onChange}
          onDropdownVisibleChange={onDropdownVisibleChange}
          placeholder="Select"
          size="large"
          optionFilterProp="label"
          disabled={disabled}
          value={value}
          options={suppliers.map((supplier) => ({
            label: supplier.name,
            value: supplier.id,
          }))}
        />
      </div>
    </Tooltip>
  )
}

function ProductPropertySelect(props: {
  disabled?: boolean
  field: ProductDefaultFieldSetting
  onChange?: (value?: string | number | null) => void
  value?: string | number | null
}) {
  const { disabled, field, onChange, value } = props
  const { user } = useAuth()
  const { data: masterData, isLoading, refetch } = useMasterData()
  const [newSelectInput, setNewSelectInput] = useState('')
  const [isAddingOption, setIsAddingOption] = useState(false)
  const { hasConfiguredOptions, options: selectOptions } = useMemo(() => {
    const property = masterData.find((item) => item.name === field.field)

    return getProductSelectOptions(
      field.options,
      property?.property_values ?? []
    )
  }, [field.field, field.options, masterData])

  const addNewSelectOption = async () => {
    const newOption = newSelectInput.trim()

    if (!newOption || !user || disabled) return

    try {
      setIsAddingOption(true)
      const response = await onAddNewPropertyValue(field, newOption, user)

      if (response?.data?.statusCode === 400) {
        notification.error({
          message: response.data.message,
          duration: 3,
          placement: 'top',
        })
      }

      await refetch()
      setNewSelectInput('')
    } finally {
      setIsAddingOption(false)
    }
  }

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={(!hasConfiguredOptions && isLoading) || isAddingOption}
      onChange={onChange}
      onSearch={setNewSelectInput}
      optionFilterProp="label"
      options={selectOptions}
      placeholder="Select"
      size="large"
      value={value}
      dropdownRender={(menu) => (
        <div
          onMouseDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {menu}
          {!disabled && !hasConfiguredOptions && field.dynamic !== false && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Space style={{ padding: '0 8px 4px', width: '100%' }}>
                <Button
                  block
                  className="product-select-add-option"
                  disabled={!newSelectInput.trim()}
                  icon={<PlusOutlined />}
                  loading={isAddingOption}
                  onClick={addNewSelectOption}
                  type="text"
                >
                  Add new
                </Button>
              </Space>
            </>
          )}
        </div>
      )}
    />
  )
}

export function renderProductSettingField(
  field: ProductDefaultFieldSetting,
  fieldNames: ProductFieldSetting[],
  options: {
    isMobile: boolean
    isSupplierUser: boolean
    showCameraButton: boolean
    shouldLockSupplierField: boolean
    supplierOptions: ProductSupplier[]
    getFieldAccess: (target: ProductFieldPermissionTarget) => ProductFieldAccess
    onSupplierDropdownVisibleChange: (open: boolean) => void
  }
) {
  const label = getFieldDisplayName(fieldNames, field.field, field.name)
  const access = options.getFieldAccess({
    field: field.field,
    propertyId: field.property_id,
  })
  const isReadOnly = access === 'read'
  const rules = [
    {
      required: field.required,
      message: 'This field is required',
    },
  ]
  const imageFormItemClassName = options.showCameraButton
    ? 'product-image-form-item product-image-form-item-with-camera'
    : 'product-image-form-item'

  if (field.field === 'supplier') {
    const isSupplierRequired = field.required || options.isSupplierUser
    const supplierRules = isSupplierRequired
      ? [{ required: true, message: 'This field is required' }]
      : []
    const isSupplierDisabled = options.shouldLockSupplierField || isReadOnly
    const supplierLockTooltip = options.shouldLockSupplierField
      ? options.isSupplierUser
        ? 'Linked to your supplier profile'
        : 'This product was created by the supplier'
      : ''

    if (access === 'hidden') return null

    if (options.isMobile) {
      return (
        <Form.Item
          key={field.field}
          name="supplier"
          label={label}
          labelCol={labelCol}
          labelAlign="left"
          required={isSupplierRequired}
          rules={supplierRules}
          {...getReadOnlyFormItemProps(isSupplierDisabled)}
        >
          <SupplierMobileSelect
            disabled={isSupplierDisabled}
            suppliers={options.supplierOptions}
            tooltipTitle={supplierLockTooltip}
          />
        </Form.Item>
      )
    }

    return (
      <Form.Item
        key={field.field}
        name="supplier"
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={isSupplierRequired}
        rules={supplierRules}
        {...getReadOnlyFormItemProps(isSupplierDisabled)}
      >
        <SupplierSelect
          disabled={isSupplierDisabled}
          onDropdownVisibleChange={options.onSupplierDropdownVisibleChange}
          suppliers={options.supplierOptions}
          tooltipTitle={supplierLockTooltip}
        />
      </Form.Item>
    )
  }

  if (access === 'hidden' && field.data_type !== 'image_description') {
    return null
  }

  if (field.field === 'picture') {
    return (
      <Form.Item
        key={field.field}
        name="picture"
        label={label}
        labelCol={labelCol}
        rules={rules}
        required={field.required}
        labelAlign="left"
        valuePropName="fileList"
        getValueFromEvent={getUploadValueFromEvent}
        {...getReadOnlyFormItemProps(isReadOnly, imageFormItemClassName)}
      >
        <ProductImageUpload
          disabled={isReadOnly}
          showCameraButton={options.showCameraButton}
        />
      </Form.Item>
    )
  }

  if (field.data_type === 'number') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        initialValue={field.value || null}
        {...getReadOnlyFormItemProps(isReadOnly)}
      >
        {/* DecimalInput bakes in inputMode="decimal" + comma/dot parsing +
            locale-aware decimal display, so "55,5" is kept instead of "555". */}
        <DecimalInput
          disabled={isReadOnly}
          placeholder="Number"
          size="large"
          onWheel={(event: any) => event.target.blur()}
        />
      </Form.Item>
    )
  }

  if (field.data_type === 'date') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        initialValue={toProductDateFormValue(field.value)}
        {...getReadOnlyFormItemProps(isReadOnly)}
      >
        <DatePicker
          disabled={isReadOnly}
          format="DD MMM YYYY"
          placeholder="Date"
          size="large"
          style={{ width: '100%' }}
        />
      </Form.Item>
    )
  }

  if (field.data_type === 'select') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        initialValue={field.value || null}
        {...getReadOnlyFormItemProps(isReadOnly)}
      >
        <ProductPropertySelect disabled={isReadOnly} field={field} />
      </Form.Item>
    )
  }

  if (field.data_type === 'image') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        valuePropName="fileList"
        getValueFromEvent={getUploadValueFromEvent}
        {...getReadOnlyFormItemProps(isReadOnly, imageFormItemClassName)}
      >
        <ProductImageUpload
          disabled={isReadOnly}
          showCameraButton={options.showCameraButton}
        />
      </Form.Item>
    )
  }

  if (field.data_type === 'file') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        initialValue={Array.isArray(field.value) ? field.value : []}
        valuePropName="fileList"
        getValueFromEvent={getFileUploadValueFromEvent}
        {...getReadOnlyFormItemProps(isReadOnly)}
      >
        <ProductFileUpload disabled={isReadOnly} />
      </Form.Item>
    )
  }

  if (field.data_type === 'image_description') {
    const descriptionAccess = field.description
      ? options.getFieldAccess({ field: field.description.field })
      : 'hidden'
    const isDescriptionReadOnly = descriptionAccess === 'read'
    const descriptionRules = [
      {
        required: field.description?.required ?? false,
        message: '',
      },
    ]

    return (
      <>
        {field.description && descriptionAccess !== 'hidden' && (
          <Form.Item
            key={field.description.field}
            name={field.description.field}
            label={getFieldDisplayName(
              fieldNames,
              field.description.field,
              field.description.name
            )}
            labelCol={labelCol}
            labelAlign="left"
            required={field.description.required ?? false}
            rules={descriptionRules}
            {...getReadOnlyFormItemProps(isDescriptionReadOnly)}
          >
            <Input
              disabled={isDescriptionReadOnly}
              placeholder="Text"
              size="large"
            />
          </Form.Item>
        )}
        {access !== 'hidden' && (
          <Form.Item
            key={field.field}
            name={field.field}
            label={label}
            labelCol={labelCol}
            labelAlign="left"
            required={field.required}
            rules={rules}
            valuePropName="fileList"
            getValueFromEvent={getUploadValueFromEvent}
            {...getReadOnlyFormItemProps(isReadOnly, imageFormItemClassName)}
          >
            <ProductImageUpload
              disabled={isReadOnly}
              showCameraButton={options.showCameraButton}
            />
          </Form.Item>
        )}
      </>
    )
  }

  if (field.data_type === 'textarea') {
    return (
      <Form.Item
        key={field.field}
        name={field.field}
        label={label}
        labelCol={labelCol}
        labelAlign="left"
        required={field.required}
        rules={rules}
        initialValue={field.value || null}
        {...getReadOnlyFormItemProps(isReadOnly)}
      >
        <Input.TextArea
          autoSize={{ minRows: 1 }}
          disabled={isReadOnly}
          placeholder="Text"
          size="large"
        />
      </Form.Item>
    )
  }

  return (
    <Form.Item
      key={field.field}
      name={field.field}
      label={label}
      labelCol={labelCol}
      labelAlign="left"
      required={field.required}
      rules={rules}
      initialValue={field.value || null}
      {...getReadOnlyFormItemProps(isReadOnly)}
    >
      <Input disabled={isReadOnly} placeholder="Text" size="large" />
    </Form.Item>
  )
}

function AddProductPage() {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const { canCreateProduct, canEditProduct } = useProductPermission(user)
  const {
    getFieldAccess: getConfiguredFieldAccess,
    isLoading: isLoadingFieldPermissions,
  } = useProductFieldPermission()
  const { id: productId } = useParams()
  const navigate = useNavigate()
  const { isMobile } = useMobileSidebar()
  const hasCoarsePointer =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches
  const showCameraButton =
    typeof navigator !== 'undefined' &&
    isPhoneOrTabletDevice(navigator, hasCoarsePointer)
  const [isCreatingProduct, setIsCreatingProduct] = useState(false)
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false)
  const hasHandledMissingProduct = useRef(false)
  const isEditMode = Boolean(productId)
  const userRole = getProductUserRole(user)
  const isSupplierUser = userRole === USER_ROLE.SUPPLIER
  const assignedSupplier = user?.suppliers?.[0] as ProductSupplier | undefined

  const { data: dataProductSettings, isFetching: isLoadingProductSettings } =
    useFreshProductSetting()

  const { data: suppliers = [], isLoading: isLoadingSuppliers } = useQuery(
    ['GET_PRODUCT_SUPPLIERS', user?.company?.id],
    () => getProductSuppliers(user?.company?.id),
    {
      enabled: shouldFetchProductSuppliers(userRole),
    }
  )
  const {
    data: productDetail,
    error: productDetailError,
    isLoading: isLoadingProductDetail,
  } = useQuery(
    ['GET_PRODUCT_DETAIL', productId],
    () => getProductDetail(productId as string),
    {
      enabled: isEditMode,
      retry: false,
    }
  )
  const productSource = getProductFieldPermissionSource({
    hasManualSource: dataProductSettings?.hasManualSource,
    isEditMode,
    productSource: productDetail?.source,
  })
  const isSupplierCreatedProduct =
    isEditMode && isProductCreatedBySupplier(productDetail)
  const getFieldAccess = useCallback(
    (target: ProductFieldPermissionTarget) =>
      getConfiguredFieldAccess({
        ...target,
        isProductCreatedBySupplier: isSupplierCreatedProduct,
        isSupplierUser,
        productSource,
      }),
    [
      getConfiguredFieldAccess,
      isSupplierCreatedProduct,
      isSupplierUser,
      productSource,
    ]
  )

  useEffect(() => {
    if (hasHandledMissingProduct.current) return

    const errorMessage = getProductRedirectErrorMessage(productDetailError)

    if (!errorMessage) return

    hasHandledMissingProduct.current = true
    message.error(errorMessage, 4)
    navigate(DEFAULT_ROUTE.PRODUCTS, { replace: true })
  }, [navigate, productDetailError])

  const defaultFields = useMemo(() => {
    const fields = dataProductSettings?.defaultFields ?? []

    return orderBy(
      fields.length ? fields : STATIC_PRODUCT_FIELDS,
      'display_order'
    )
  }, [dataProductSettings])
  const fieldNames = dataProductSettings?.fieldNames ?? []
  const supplierOptions = useMemo(() => {
    if (isSupplierUser && assignedSupplier) return [assignedSupplier]

    if (
      isEditMode &&
      productDetail?.supplier &&
      !suppliers.some((supplier) => supplier.id === productDetail.supplier?.id)
    ) {
      return [productDetail.supplier, ...suppliers]
    }

    return suppliers
  }, [isSupplierUser, assignedSupplier, isEditMode, productDetail, suppliers])
  const shouldLockSupplierField = isSupplierUser || isSupplierCreatedProduct

  useEffect(() => {
    if (!isMobile || !isSupplierDropdownOpen) return

    const scrollY = window.scrollY
    const siteLayout = document.querySelector<HTMLElement>('.site-layout')
    const originalBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    }
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalSiteLayoutOverflow = siteLayout?.style.overflow

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'

    if (siteLayout) {
      siteLayout.style.overflow = 'hidden'
    }

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow
      document.body.style.overflow = originalBodyStyle.overflow
      document.body.style.position = originalBodyStyle.position
      document.body.style.top = originalBodyStyle.top
      document.body.style.width = originalBodyStyle.width

      if (siteLayout) {
        siteLayout.style.overflow = originalSiteLayoutOverflow ?? ''
      }

      window.scrollTo(0, scrollY)
    }
  }, [isMobile, isSupplierDropdownOpen])

  useEffect(() => {
    if (!isSupplierUser || !assignedSupplier?.id) return

    form.setFieldValue('supplier', assignedSupplier.id)
  }, [isSupplierUser, assignedSupplier?.id])

  useEffect(() => {
    if (!isEditMode || !productDetail) return

    form.setFieldsValue(
      transformProductDetailToFormValue(productDetail, defaultFields)
    )
  }, [isEditMode, productDetail, defaultFields, form])

  useEffect(() => {
    if (!isEditMode) {
      if (!canCreateProduct()) {
        message.error('You do not have permission to create products', 4)
        navigate(DEFAULT_ROUTE.PRODUCTS)
      }
      return
    }

    if (!productDetail || isLoadingProductDetail) return

    if (!canEditProduct(productDetail)) {
      message.error('You do not have permission to edit this product', 4)
      navigate(`/products/${productId}`)
    }
  }, [
    isEditMode,
    productDetail,
    isLoadingProductDetail,
    user,
    canCreateProduct,
    canEditProduct,
    navigate,
    productId,
  ])

  const displayValidationErrorMessage = () => {
    message.error('Please input all required fields', 3)
  }
  const isLoading =
    isLoadingProductSettings ||
    isLoadingSuppliers ||
    isLoadingProductDetail ||
    isLoadingFieldPermissions
  const onGoBack = () => {
    navigate(-1)
  }

  const onSubmitProduct = async () => {
    let submissionStage = 'permission_check'
    let submittedFormValues: Record<string, any> = {}
    let productPayload: ProductCreatePayload | ProductUpdatePayload | undefined

    try {
      if (!isEditMode && !canCreateProduct()) return
      if (isEditMode && !canEditProduct(productDetail)) return

      setIsCreatingProduct(true)
      submissionStage = 'form_validation'
      await form.validateFields()
      const values = form.getFieldsValue(true)
      const trimmedValues = trimFormInputValues(values)
      submittedFormValues = trimmedValues
      form.setFieldsValue(trimmedValues)
      await form.validateFields()
      const source = dataProductSettings?.hasManualSource ? 'manual' : ''

      submissionStage = 'prepare_product_payload'

      if (isEditMode && productId) {
        const payload = await prepareUpdateProductPayload(
          trimmedValues,
          defaultFields,
          getFieldAccess,
          { productId }
        )
        productPayload = payload
        submissionStage = 'update_product_request'
        await updateProduct(productId, payload)
      } else {
        const payload = await prepareCreateProductPayload(
          trimmedValues,
          defaultFields,
          getFieldAccess,
          { source }
        )
        productPayload = payload
        submissionStage = 'create_product_request'
        await createProduct(payload)
      }

      message.success(isEditMode ? 'Product updated.' : 'Product created.', 3)
      navigate(-1)
    } catch (error: any) {
      console.error('Product submission failed', {
        error,
        submissionStage,
      })

      if (error?.errorFields) {
        displayValidationErrorMessage()
      } else {
        const currentFormValues = Object.keys(submittedFormValues).length
          ? submittedFormValues
          : form.getFieldsValue(true)
        const productNumber = currentFormValues?.number || 'unknown'
        const userId = user?.id || 'unknown'
        const companyId = user?.company?.id || 'unknown'
        const status = error?.response?.status
        const rawServerMessage = error?.response?.data?.message
        const serverMessage = Array.isArray(rawServerMessage)
          ? rawServerMessage.join('. ')
          : rawServerMessage
        const isImageUpload = Boolean(error?.isProductImageUpload)

        Sentry.withScope((scope) => {
          scope.setLevel('error')
          scope.setTags({
            'product.operation': isEditMode ? 'update' : 'create',
            'product.number': String(productNumber),
            'user.id': String(userId),
            'company.id': String(companyId),
            'submission.stage': submissionStage,
            'submission.step': isImageUpload ? 'image-upload' : 'submit',
            'http.status': status ? String(status) : 'none',
          })
          scope.setUser({ id: String(userId) })
          scope.setContext('product_submission', {
            operation: isEditMode ? 'update' : 'create',
            stage: submissionStage,
            product_id: productId,
            product_number: productNumber,
            user_id: userId,
            company_id: companyId,
            user_role: userRole,
            browser_online: navigator.onLine,
            page_url: window.location.href,
          })
          scope.setContext('form_values', {
            values: sanitizeSentryValue(currentFormValues),
          })
          scope.setContext('product_payload', {
            payload: sanitizeSentryValue(productPayload),
          })
          scope.setContext('request', {
            method: error?.config?.method,
            url: error?.config?.url,
            base_url: error?.config?.baseURL,
            timeout: error?.config?.timeout,
            params: sanitizeSentryValue(error?.config?.params),
          })
          scope.setContext('backend_response', {
            status: error?.response?.status,
            status_text: error?.response?.statusText,
            data: sanitizeSentryValue(error?.response?.data),
          })
          scope.setContext('caught_error', {
            name: error?.name,
            message: error?.message,
            code: error?.code,
            is_axios_error: error?.isAxiosError,
            is_image_upload: isImageUpload,
          })
          Sentry.captureException(error)
        })

        const action = isEditMode ? 'updated' : 'created'
        let userMessage: string
        if (serverMessage) {
          // Backend returned a real reason (e.g. "Supplier not found") — show it.
          userMessage = serverMessage
        } else if (status === 413) {
          userMessage =
            'The image is too large to upload. Please use a smaller image and try again.'
        } else if (status === 502 || status === 503 || status === 504) {
          userMessage =
            'The server is temporarily unavailable. Please try again in a moment.'
        } else if (!error?.response) {
          // No HTTP response at all: network/CORS/timeout, or a non-API error
          // thrown before the request (most often the image upload step).
          userMessage = isImageUpload
            ? 'The product image could not be uploaded. Please check your connection and image size, then try again.'
            : `Could not reach the server, so the product was not ${action}. Please check your connection and try again.`
        } else {
          userMessage = isEditMode
            ? 'Product cannot be updated'
            : 'Product cannot be created'
        }

        message.error(userMessage, 4)
      }
    } finally {
      setIsCreatingProduct(false)
    }
  }

  return (
    <div className="add-product-page">
      {isMobile ? (
        <Header
          icon={<TagOutlined className="icon" />}
          title=""
          background="linear-gradient(181.35deg, #b1579c 1.15%,#da7ac4 98.85%)"
        />
      ) : (
        <BackHeader onClick={onGoBack} title="Back" />
      )}
      <div className="add-product-page-background">
        <div className="pbf-edit-order-container add-product-page-container">
          <div className="header">
            <p>{isEditMode ? 'Edit Product' : 'New Product'}</p>
          </div>

          <Spin spinning={isLoading}>
            <div className="pbf-order-detail-form product-detail-form">
              <Form form={form} requiredMark className="row-container">
                {!isLoadingFieldPermissions &&
                  defaultFields.map((field) =>
                    renderProductSettingField(field, fieldNames, {
                      isMobile,
                      isSupplierUser,
                      showCameraButton,
                      shouldLockSupplierField,
                      supplierOptions,
                      getFieldAccess,
                      onSupplierDropdownVisibleChange:
                        setIsSupplierDropdownOpen,
                    })
                  )}
              </Form>

              <div className="product-form-actions">
                <Button
                  className="product-cancel-button"
                  size="large"
                  onClick={onGoBack}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  loading={isCreatingProduct}
                  onClick={onSubmitProduct}
                >
                  {isEditMode ? 'Save' : 'Create'}
                </Button>
              </div>
            </div>
          </Spin>
        </div>
      </div>
    </div>
  )
}

export default AddProductPage
